/**
 * The Mapping tab: how the client's data became the tables.
 *
 * Three columns joined by lines: the sheets their brief describes (and which
 * of them reached us), the files that actually arrived, and the tables built
 * from them. Hovering a card lights the lines through it. Everything is read
 * from the manifest the normalizer wrote beside the tables.
 */

import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { BriefSheet, Portfolio } from "../../services/portfolioDataService";

export interface PortfolioMappingProps {
	portfolio: Portfolio;
}

/** Only the sheets that did not fully arrive say so; "arrived" is the default. */
const ARRIVED: Record<BriefSheet["arrived"], { label: string; text: string } | null> = {
	yes: null,
	part: { label: "in part", text: "text-[#b54708]" },
	no: { label: "not sent", text: "text-[#9a9ea3]" },
};

/** Our tables, in the client's words: what each is on this page. */
const TABLE_NAME: Record<string, { name: string; note: string }> = {
	house: { name: "Houses", note: "one row per house" },
	owner: { name: "Owners", note: "one row per owner of a house" },
	capital_component: { name: "Capital items", note: "each appliance and big-ticket item, by house" },
	fact: { name: "Yearly figures by house", note: "rent, expenses and income, per house and year" },
	plat_fact: { name: "Plat statements", note: "NOI, cap rate and DCR, per plat and year" },
	loan: { name: "Loans", note: "one row per plat loan" },
	line_item: { name: "Data guide", note: "what every figure means (new)" },
};

interface Edge {
	from: string;
	to: string;
}

const CARD = "rounded-[10px] border bg-white px-3 py-2.5 text-left";

/** Lines between cards, measured from the DOM so they follow the layout. */
const MapGraph: React.FC<{ portfolio: Portfolio }> = ({ portfolio }) => {
	const { manifest, tables } = portfolio;
	// Each brief sheet reached us inside the file its first table was built from.
	const sourceOf = (table: string | undefined) =>
		manifest.flows.find((flow) => flow.to === table)?.from ?? null;
	const fileFor = (sheet: BriefSheet) => (sheet.arrived === "no" ? null : sourceOf(sheet.to[0]));

	const edges: Edge[] = useMemo(() => {
		const out: Edge[] = [];
		for (const sheet of manifest.brief_sheets) {
			const file = fileFor(sheet);
			if (file) out.push({ from: `sheet:${sheet.sheet}`, to: `file:${file}` });
		}
		for (const flow of manifest.flows) {
			if (flow.from) out.push({ from: `file:${flow.from}`, to: `table:${flow.to}` });
		}
		return out;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [manifest]);

	const root = useRef<HTMLDivElement>(null);
	const nodes = useRef(new Map<string, HTMLElement>());
	const [paths, setPaths] = useState<{ key: string; d: string; from: string; to: string }[]>([]);
	const [size, setSize] = useState({ w: 0, h: 0 });
	const [hover, setHover] = useState<string | null>(null);

	useLayoutEffect(() => {
		const measure = () => {
			const box = root.current?.getBoundingClientRect();
			if (!box) return;
			setSize({ w: box.width, h: box.height });
			setPaths(
				edges.flatMap((edge) => {
					const a = nodes.current.get(edge.from)?.getBoundingClientRect();
					const b = nodes.current.get(edge.to)?.getBoundingClientRect();
					if (!a || !b) return [];
					const x1 = a.right - box.left;
					const y1 = a.top + a.height / 2 - box.top;
					const x2 = b.left - box.left;
					const y2 = b.top + b.height / 2 - box.top;
					const mid = (x1 + x2) / 2;
					return [
						{
							key: `${edge.from}->${edge.to}`,
							d: `M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2},${y2}`,
							from: edge.from,
							to: edge.to,
						},
					];
				}),
			);
		};
		measure();
		if (typeof ResizeObserver === "undefined" || !root.current) return;
		const observer = new ResizeObserver(measure);
		observer.observe(root.current);
		return () => observer.disconnect();
	}, [edges]);

	const register = (id: string) => (el: HTMLElement | null) => {
		if (el) nodes.current.set(id, el);
		else nodes.current.delete(id);
	};
	// What a hovered card is about: a sheet, its file and the tables it feeds;
	// a table, its file and the sheets that feed it; a file, everything through it.
	const related = useMemo(() => {
		if (hover === null) return null;
		const ids = new Set<string>([hover]);
		const [kind, name] = [hover.slice(0, hover.indexOf(":")), hover.slice(hover.indexOf(":") + 1)];
		for (const sheet of manifest.brief_sheets) {
			const file = fileFor(sheet);
			const touches =
				(kind === "sheet" && sheet.sheet === name) ||
				(kind === "table" && sheet.to.includes(name)) ||
				(kind === "file" && file === name);
			if (!touches || !file) continue;
			ids.add(`sheet:${sheet.sheet}`);
			ids.add(`file:${file}`);
			for (const table of sheet.to) if (kind !== "table" || table === name) ids.add(`table:${table}`);
		}
		if (kind === "table" || kind === "file") {
			for (const flow of manifest.flows) {
				if (!flow.from) continue;
				if ((kind === "table" && flow.to === name) || (kind === "file" && flow.from === name)) {
					ids.add(`file:${flow.from}`);
					ids.add(`table:${flow.to}`);
				}
			}
		}
		return ids;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hover, manifest]);
	const lit = (id: string) => related?.has(id) ?? false;
	const edgeLit = (p: { from: string; to: string }) =>
		related !== null && related.has(p.from) && related.has(p.to);
	const hoverProps = (id: string) => ({
		onMouseEnter: () => setHover(id),
		onMouseLeave: () => setHover(null),
		onFocus: () => setHover(id),
		onBlur: () => setHover(null),
		tabIndex: 0,
	});
	const dim = (id: string) => (hover !== null && !lit(id) ? "opacity-45" : "");

	const rowsOf = (table: string) =>
		(tables as Record<string, unknown[]>)[table]?.length ?? manifest.flows.find((f) => f.to === table)?.rows ?? 0;
	const tableNames = manifest.flows.map((flow) => flow.to);
	const groups = ["Property Analysis", "Financial Analysis"];

	return (
		<div className="overflow-x-auto rounded-xl border border-gray-200 bg-[#fcfcfb] p-5">
			<div ref={root} className="relative grid min-w-[980px] grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)_minmax(0,1fr)] gap-x-24">
				<svg
					aria-hidden
					className="pointer-events-none absolute inset-0"
					width={size.w}
					height={size.h}
				>
					{paths.map((p) => (
						<path
							key={p.key}
							d={p.d}
							fill="none"
							stroke={edgeLit(p) ? "#008782" : "#c8d3d2"}
							strokeWidth={edgeLit(p) ? 2 : 1.25}
						/>
					))}
				</svg>

				<div className="relative flex flex-col gap-3">
					<h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">
						Your brief · {manifest.brief_sheets.length} sheets
					</h3>
					{groups.map((group) => (
						<div key={group} className="flex flex-col gap-1.5">
							<p className="text-[12.5px] font-semibold text-[#1F2937]">{group}</p>
							{manifest.brief_sheets
								.filter((sheet) => sheet.source === group)
								.map((sheet) => {
									const id = `sheet:${sheet.sheet}`;
									return (
										<div
											key={sheet.sheet}
											ref={register(id)}
											{...hoverProps(id)}
											className={`flex items-center justify-between gap-2 rounded-[10px] border bg-white px-3 py-1.5 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] ${
												sheet.arrived === "no" ? "border-dashed border-gray-300 bg-transparent" : "border-gray-200"
											} ${dim(id)}`}
											title={sheet.note}
										>
											<span className={`truncate text-[13px] ${sheet.arrived === "no" ? "text-[#9a9ea3]" : "text-[#111111]"}`}>
												{sheet.sheet}
											</span>
											{ARRIVED[sheet.arrived] && (
												<span className={`flex-shrink-0 text-[11.5px] ${ARRIVED[sheet.arrived]?.text}`}>
													{ARRIVED[sheet.arrived]?.label}
												</span>
											)}
										</div>
									);
								})}
						</div>
					))}
				</div>

				<div className="relative flex flex-col justify-around gap-6 pt-8">
					<h3 className="absolute top-0 text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">
						What arrived · {manifest.arrived.length} files
					</h3>
					{manifest.arrived.map((file) => {
						const id = `file:${file.file}`;
						return (
							<div
								key={file.file}
								ref={register(id)}
								{...hoverProps(id)}
								className={`${CARD} border-[#e8d9c2] bg-[#fbf7f1] transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] ${dim(id)}`}
							>
								<p className="break-words text-[13px] font-semibold text-[#111111]">{file.file}</p>
								<p className="mt-0.5 text-[12px] text-[#5B6472]">{file.shape}</p>
							</div>
						);
					})}
				</div>

				<div className="relative flex flex-col gap-2">
					<h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">
						How Doppler holds it · {tableNames.length} tables
					</h3>
					{tableNames.map((name) => {
						const id = `table:${name}`;
						return (
							<div
								key={name}
								ref={register(id)}
								{...hoverProps(id)}
								className={`${CARD} border-[#99CFCD] transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] ${dim(id)}`}
							>
								<div className="flex items-baseline justify-between gap-2">
									<span className="text-[13px] font-semibold text-[#004C4B]">{TABLE_NAME[name]?.name ?? name}</span>
									<span className="text-[11.5px] tabular-nums text-[#6B7280]">
										{Number(rowsOf(name)).toLocaleString()} rows
									</span>
								</div>
								<p className="text-[11.5px] text-[#5B6472]">{TABLE_NAME[name]?.note ?? ""}</p>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

const PortfolioMapping: React.FC<PortfolioMappingProps> = ({ portfolio }) => {
	const { manifest } = portfolio;
	const tableRows = manifest.flows.reduce((sum, flow) => sum + flow.rows, 0);
	return (
		<div className="flex flex-col gap-3">
			<p className="text-sm text-[#5B6472]">
				{manifest.brief_sheets.length} sheets in your brief arrived as {manifest.arrived.length} workbooks. We
				turned them into {manifest.flows.length} tables ({tableRows.toLocaleString()} rows), one row per figure,
				each keeping the workbook cell it came from. Hover a card to follow it.
			</p>
			<MapGraph portfolio={portfolio} />
		</div>
	);
};

export default PortfolioMapping;
